import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MascaraCpf } from "../../../utils/MascaraCpf";
import { MascaraRg } from "../../../utils/MascaraRg";
import { MascaraTituloEleitor } from "../../../utils/MascaraTituloEleitor";
import { MascaraCnh } from "../../../utils/MascaraCnh";
import { MascaraTelefone } from "../../../utils/MascaraTelefone";
import { MascaraCep } from "../../../utils/MascaraCep";
import { MascaraCnpj } from "../../../utils/MascaraCnpj";
import axios from "axios";

const PURPLE = "#9C01B9";
const GREEN = "#087F3F";
const TEXT = "#171717";
const MUTED = "#667085";
const BORDER = "#E5E7EB";
const BG = "#F5F7F9";

const steps = [
    "Informações Pessoais",
    "Contato e Endereço",
    "Status Conjugal",
    "Informações da Empresa",
    "Informações Societárias",
    "Confirmação",
];


function DadosPessoais({
    form,
    errors,
    handleChange,
}) {
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 2,
                }}
            >
                Informações Pessoais
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {/* NOME COMPLETO */}
                <Campo
                    label="Nome Completo *"
                    placeholder="Ex: Ana Clara Ferreira"
                    value={form.nome}
                    onChange={handleChange("nome")}
                    error={errors.nome}
                    fullWidth
                />

                {/* CPF + DATA DE NASCIMENTO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="CPF *"
                            placeholder="000.000.000-00"
                            value={form.cpf}
                            onChange={(e) => { handleChange("cpf")({ target: { value: MascaraCpf(e.target.value), }, }); }}
                            error={errors.cpf}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Data de Nascimento *"
                            type="date"
                            value={form.dataNascimento}
                            onChange={handleChange(
                                "dataNascimento"
                            )}
                            error={errors.dataNascimento}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box>
                </Box>

                {/* RG + ÓRGÃO EXPEDIDOR */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="RG *"
                            placeholder="00.000.000-0"
                            value={form.rg}
                            onChange={(e) => { handleChange("rg")({ target: { value: MascaraRg(e.target.value), }, }); }}
                            error={errors.rg}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Órgão Expedidor do RG *"
                            placeholder="Ex: SSP"
                            value={form.orgaoRg}
                            onChange={handleChange(
                                "orgaoRg"
                            )}
                            error={errors.orgaoRg}
                        />
                    </Box>
                </Box>

                {/* TÍTULO DE ELEITOR + PIS/NIS/NIT */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Título de Eleitor"
                            placeholder="0000 0000 0000"
                            value={form.tituloEleitor}
                            onChange={(e) => { handleChange("tituloEleitor")({ target: { value: MascaraTituloEleitor(e.target.value), }, }); }}
                            error={errors.tituloEleitor}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="PIS / NIS / NIT"
                            placeholder="000.00000.00-0"
                            value={form.pisNisNit}
                            onChange={handleChange(
                                "pisNisNit"
                            )}
                            error={errors.pisNisNit}
                        />
                    </Box>
                </Box>

                {/* CNH + ESTADO DE EMISSÃO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="CNH"
                            placeholder="00000000000"
                            value={form.cnh}
                            onChange={(e) => { handleChange("cnh")({ target: { value: MascaraCnh(e.target.value), }, }); }}
                            error={errors.cnh}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Estado de Emissão da CNH"
                            placeholder="Ex: SP"
                            value={form.estadoEmissaoCnh}
                            onChange={handleChange(
                                "estadoEmissaoCnh"
                            )}
                            error={
                                errors.estadoEmissaoCnh
                            }
                        />
                    </Box>
                </Box>

                {/* NATURALIDADE */}
                <Campo
                    label="Naturalidade *"
                    placeholder="Ex: São Paulo - SP"
                    value={form.naturalidade}
                    onChange={handleChange("naturalidade")}
                    error={errors.naturalidade}
                    fullWidth
                />
            </Box>
        </Box>
    );
}



function InformacoesEmpresa({
    form,
    errors,
    handleChange,
}) {
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 2,
                }}
            >
                Informações da Empresa
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {/* CNPJ + SENHA GOV */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="CNPJ"
                            placeholder="00.000.000/0000-00"
                            value={form.cnpj}
                            onChange={(e) => { handleChange("cnpj")({ target: { value: MascaraCnpj(e.target.value), }, }); }}
                            error={errors.cnpj}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Senha Gov.br"
                            placeholder="Digite a senha do Gov.br"
                            type="password"
                            value={form.senhaGov}
                            onChange={handleChange("senhaGov")}
                            error={errors.senhaGov}
                            autoComplete="new-password"
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Capital Social"
                            placeholder="Digite o capital social da empresa"
                            type="text"
                            value={form.capitalSocial}
                            onChange={handleChange("capitalSocial")}
                            error={errors.capitalSocial}
                        />
                    </Box>
                </Box>

                {/* RAZÃO SOCIAL */}
                <Campo
                    label="Razão Social *"
                    placeholder="Ex: Empresa Exemplo Ltda."
                    value={form.razaoSocial}
                    onChange={handleChange("razaoSocial")}
                    error={errors.razaoSocial}
                    fullWidth
                    helperText="Razão social é o nome jurídico da empresa"
                />

                {/* NOME FANTASIA */}
                <Campo
                    label="Nome Fantasia *"
                    placeholder="Ex: Empresa Exemplo"
                    value={form.nomeFantasia}
                    onChange={handleChange("nomeFantasia")}
                    error={errors.nomeFantasia}
                    fullWidth
                    helperText="Nome fantasia é o nome pelo qual a empresa é conhecida"
                />

                {/* ENDEREÇO FISCAL */}
                <Campo
                    label="Endereço Fiscal *"
                    placeholder="Ex: Rua das Flores, 123 - Centro, São Paulo - SP"
                    value={form.enderecoFiscal}
                    onChange={handleChange("enderecoFiscal")}
                    error={errors.enderecoFiscal}
                    fullWidth
                />
            </Box>
        </Box>
    );
}




function Societario({
    form,
    errors,
    handleChange,
}) {
    const percentual = Number(form.percentual || 0);

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 2,
                }}
            >
                Dados Societários
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                {/* <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    {/* <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Tipo de Sócio *"
                            select
                            value={form.tipoSocio}
                            onChange={handleChange(
                                "tipoSocio"
                            )}
                            SelectProps={{
                                native: true,
                            }}
                        >
                            <option>
                                Sócio Quotista
                            </option>
                            <option>
                                Sócio Administrador
                            </option>
                        </Campo>
                    </Box> */}

                {/* <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Data de Entrada *"
                            type="date"
                            value={form.dataEntrada}
                            onChange={handleChange(
                                "dataEntrada"
                            )}
                            error={errors.dataEntrada}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box> */}
                {/* </Box>  */}

                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Participação Societária *"
                            type="number"
                            placeholder="Ex: 35"
                            value={form.percentual}
                            onChange={handleChange(
                                "percentual"
                            )}
                            error={errors.percentual}
                            InputProps={{
                                endAdornment: (
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: MUTED,
                                        }}
                                    >
                                        %
                                    </Typography>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Quantidade de Quotas"
                            type="number"
                            placeholder="Calculado automaticamente"
                            value={
                                form.quantidadeQuotas ||
                                (percentual > 0
                                    ? Math.round(
                                        percentual *
                                        1000
                                    )
                                    : "")
                            }
                            onChange={handleChange(
                                "quantidadeQuotas"
                            )}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Box>
                </Box>

                {/* RESUMO */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: "9px",
                        backgroundColor: "#F7F8FA",
                        border: `1px solid ${BORDER}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: MUTED,
                            }}
                        >
                            Participação selecionada
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: PURPLE,
                                mt: 0.3,
                            }}
                        >
                            {percentual.toFixed(2)}%
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            textAlign: {
                                xs: "left",
                                sm: "right",
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: MUTED,
                            }}
                        >
                            Quotas estimadas
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: TEXT,
                                mt: 0.3,
                            }}
                        >
                            {(
                                percentual * 1000
                            ).toLocaleString("pt-BR")}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export function Confirmacao({
    form,
    titulo = "Confirme os dados do sócio",
    mostrarAviso = true,
}) {
    return (
        <Box>
            {titulo && (
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: TEXT,
                        mb: 2,
                    }}
                >
                    {titulo}
                </Typography>
            )}

            {/* =========================
                INFORMAÇÕES PESSOAIS
            ========================= */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 1,
                    mt: 1,
                }}
            >
                Informações Pessoais
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <ResumoItem
                    label="Nome completo"
                    value={form.nome}
                />

                <ResumoItem
                    label="CPF"
                    value={form.cpf}
                />

                <ResumoItem
                    label="Data de nascimento"
                    value={form.dataNascimento}
                />

                <ResumoItem
                    label="RG"
                    value={form.rg}
                />

                <ResumoItem
                    label="Órgão expedidor do RG"
                    value={form.orgaoRg}
                />

                <ResumoItem
                    label="Título de eleitor"
                    value={form.tituloEleitor}
                />

                <ResumoItem
                    label="PIS / NIS / NIT"
                    value={form.pisNisNit}
                />

                <ResumoItem
                    label="CNH"
                    value={form.cnh}
                />

                <ResumoItem
                    label="Estado de emissão da CNH"
                    value={form.estadoEmissaoCnh}
                />

                <ResumoItem
                    label="Naturalidade"
                    value={form.naturalidade}
                />
            </Box>

            {/* =========================
                CONTATO E ENDEREÇO
            ========================= */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 1,
                    mt: 2.5,
                }}
            >
                Contato e Endereço
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <ResumoItem
                    label="E-mail"
                    value={form.email}
                />

                <ResumoItem
                    label="Telefone"
                    value={form.telefone}
                />

                <ResumoItem
                    label="CEP"
                    value={form.cep}
                />

                <ResumoItem
                    label="Endereço"
                    value={[
                        form.rua,
                        form.numero,
                        form.complemento,
                        form.bairro,
                        form.cidade,
                        form.estado,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                />
            </Box>

            {/* =========================
                STATUS CONJUGAL
            ========================= */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 1,
                    mt: 2.5,
                }}
            >
                Status Conjugal
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <ResumoItem
                    label="Estado civil"
                    value={form.estadoCivil}
                />

                <ResumoItem
                    label="Regime de bens"
                    value={form.regimeBens || "Não informado"}
                />

                <ResumoItem
                    label="CPF do cônjuge"
                    value={form.cpfConjugue || "Não informado"}
                />
            </Box>

            {/* =========================
                INFORMAÇÕES DA EMPRESA
            ========================= */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 1,
                    mt: 2.5,
                }}
            >
                Informações da Empresa
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <ResumoItem
                    label="CNPJ"
                    value={form.cnpj}
                />

                <ResumoItem
                    label="Razão Social"
                    value={form.razaoSocial}
                />

                <ResumoItem
                    label="Nome Fantasia"
                    value={form.nomeFantasia || "Não informado"}
                />

                <ResumoItem
                    label="Endereço Fiscal"
                    value={form.enderecoFiscal}
                />
            </Box>

            {/* =========================
                AVISO
            ========================= */}
            {mostrarAviso && (
                <Box
                    sx={{
                        mt: 2.5,
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: "#EAF9F1",
                        color: GREEN,
                        fontSize: 10,
                        fontWeight: 500,
                        lineHeight: 1.5,
                    }}
                >
                    Confira todas as informações antes de adicionar o sócio.
                    Após a confirmação, os dados serão incorporados ao cadastro
                    societário da empresa.
                </Box>
            )}
        </Box>
    );
}



function ResumoItem({ label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 0.8,
                borderBottom: `1px solid ${BORDER}`,
            }}
        >
            <Typography
                sx={{
                    fontSize: 10,
                    color: MUTED,
                }}
            >
                {label}
            </Typography>

            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: TEXT,
                    textAlign: "right",
                }}
            >
                {value || "-"}
            </Typography>
        </Box>
    );
}

function Campo({
    label,
    error,
    helperText,
    children,
    ...props
}) {
    return (
        <Box sx={{ width: "100%" }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: TEXT,
                    mb: 0.6,
                }}
            >
                {label}
            </Typography>

            <TextField
                {...props}
                fullWidth
                error={Boolean(error)}
                helperText={error || helperText}
                size="small"
                sx={{
                    "& .MuiOutlinedInput-root": {
                        minHeight: 38,
                        borderRadius: "8px",
                        backgroundColor: "#F1F3F5",
                        fontSize: 15,
                        "& fieldset": {
                            borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                            borderColor: "#D8DCE1",
                        },
                        "&.Mui-focused": {
                            backgroundColor: "#FFF",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: PURPLE,
                        },
                    },

                    "& .MuiOutlinedInput-input": {
                        py: 1,
                        px: 1.3,
                    },

                    "& .MuiFormHelperText-root": {
                        fontSize: 15,
                        marginLeft: 0,
                        marginTop: 0.4,
                    },
                }}
            >
                {children}
            </TextField>
        </Box>
    );
}

function Endereco({
    form,
    errors,
    handleChange,
}) {
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 2,
                }}
            >
                Contato e Endereço
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {/* E-MAIL + TELEFONE */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="E-mail *"
                            placeholder="socio@empresa.com.br"
                            type="email"
                            value={form.email}
                            onChange={handleChange("email")}
                            error={errors.email}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Telefone / WhatsApp *"
                            placeholder="(11) 98765-4321"
                            value={form.telefone}
                            onChange={(e) => { handleChange("telefone")({ target: { value: MascaraTelefone(e.target.value), }, }); }}
                            error={errors.telefone}
                        />
                    </Box>
                </Box>

                {/* CEP + RUA + NÚMERO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                sm: 150,
                            },
                        }}
                    >
                        <Campo
                            label="CEP *"
                            placeholder="00000-000"
                            value={form.cep}
                            onChange={(e) => { handleChange("cep")({ target: { value: MascaraCep(e.target.value), }, }); }}
                            error={errors.cep}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Rua *"
                            placeholder="Rua, Avenida..."
                            value={form.rua}
                            onChange={handleChange("rua")}
                            error={errors.rua}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                sm: 120,
                            },
                        }}
                    >
                        <Campo
                            label="Número *"
                            placeholder="123"
                            value={form.numero}
                            onChange={handleChange("numero")}
                            error={errors.numero}
                        />
                    </Box>
                </Box>

                {/* BAIRRO + COMPLEMENTO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Bairro *"
                            placeholder="Centro"
                            value={form.bairro}
                            onChange={handleChange("bairro")}
                            error={errors.bairro}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Complemento"
                            placeholder="Sala, conjunto, apartamento..."
                            value={form.complemento}
                            onChange={handleChange(
                                "complemento"
                            )}
                            error={errors.complemento}
                        />
                    </Box>
                </Box>

                {/* CIDADE + ESTADO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Campo
                            label="Cidade *"
                            placeholder="São Paulo"
                            value={form.cidade}
                            onChange={handleChange("cidade")}
                            error={errors.cidade}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                sm: 150,
                            },
                        }}
                    >
                        <Campo
                            label="Estado *"
                            placeholder="SP"
                            value={form.estado}
                            onChange={handleChange("estado")}
                            error={errors.estado}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}


function EstadoCivil({
    form,
    errors,
    handleChange,
}) {
    const possuiConjuge = [
        "Casado",
        "União Estável",
    ].includes(form.estadoCivil);

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT,
                    mb: 2,
                }}
            >
                Estado Civil
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {/* ESTADO CIVIL */}
                <Campo
                    label="Estado Civil *"
                    select
                    value={form.estadoCivil}
                    onChange={handleChange("estadoCivil")}
                    error={errors.estadoCivil}
                    SelectProps={{
                        native: true,
                    }}
                >
                    <option value="">
                        Selecione o estado civil
                    </option>

                    <option value="Solteiro">
                        Solteiro(a)
                    </option>

                    <option value="Casado">
                        Casado(a)
                    </option>

                    <option value="Divorciado">
                        Divorciado(a)
                    </option>

                    <option value="Viúvo">
                        Viúvo(a)
                    </option>

                    <option value="Separado">
                        Separado(a)
                    </option>

                    <option value="União Estável">
                        União Estável
                    </option>
                </Campo>

                {/* REGIME DE BENS */}
                <Campo
                    label="Regime de Bens *"
                    select
                    value={form.regimeBens}
                    onChange={handleChange("regimeBens")}
                    error={errors.regimeBens}
                    disabled={!possuiConjuge}
                    SelectProps={{
                        native: true,
                    }}
                >
                    <option value="">
                        Selecione o regime de bens
                    </option>

                    <option value="Comunhão Parcial de Bens">
                        Comunhão Parcial de Bens
                    </option>

                    <option value="Comunhão Universal de Bens">
                        Comunhão Universal de Bens
                    </option>

                    <option value="Separação Convencional de Bens">
                        Separação Convencional de Bens
                    </option>

                    <option value="Separação Obrigatória de Bens">
                        Separação Obrigatória de Bens
                    </option>

                    <option value="Participação Final nos Aquestos">
                        Participação Final nos Aquestos
                    </option>
                </Campo>

                {/* CPF DO CÔNJUGE */}
                {possuiConjuge && (
                    <Campo
                        label="CPF do Cônjuge *"
                        placeholder="000.000.000-00"
                        value={form.cpfConjugue}
                        onChange={(e) => { handleChange("cpfConjugue")({ target: { value: MascaraCpf(e.target.value), }, }); }}
                        error={errors.cpfConjugue}
                    />
                )}

                {/* INFORMAÇÃO */}
                {possuiConjuge && (
                    <Box
                        sx={{
                            mt: 0.5,
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: "#F7F8FA",
                            border: `1px solid ${BORDER}`,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: MUTED,
                                lineHeight: 1.5,
                            }}
                        >
                            As informações do cônjuge serão
                            utilizadas para a composição dos
                            dados societários e do contrato
                            social.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

const initialForm = {
    // =========================
    // INFORMAÇÕES PESSOAIS
    // =========================
    nome: "",
    cpf: "",
    dataNascimento: "",
    rg: "",
    orgaoRg: "",
    tituloEleitor: "",
    pisNisNit: "",
    cnh: "",
    estadoEmissaoCnh: "",
    naturalidade: "",

    // =========================
    // CONTATO E ENDEREÇO
    // =========================
    email: "",
    telefone: "",
    cep: "",
    rua: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    complemento: "",

    // =========================
    // STATUS CONJUGAL
    // =========================
    estadoCivil: "",
    regimeBens: "",
    cpfConjugue: "",

    // =========================
    // INFORMAÇÕES DA EMPRESA
    // =========================
    cnpj: "",
    senhaGov: "",
    enderecoFiscal: "",
    razaoSocial: "",
    nomeFantasia: "",
    percentual: "",
    capitalSocial: "",
};

// Converte o sócio vindo da API para o formato do formulário
export const socioParaForm = (socio) => {
    const form = { ...initialForm };

    Object.keys(initialForm).forEach((campo) => {
        const valor = socio[campo];
        form[campo] = valor === null || valor === undefined ? "" : String(valor);
    });

    // input type="date" só aceita yyyy-mm-dd
    if (form.dataNascimento) {
        form.dataNascimento = form.dataNascimento.slice(0, 10);
    }

    return { ...form, id: socio.id };
};

export default function AdicionarSocioModal({ open, onClose, onSave, socio }) {
    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState(initialForm);
    const editando = Boolean(socio);

    useEffect(() => {
        if (open) {
            setForm(socio ? socioParaForm(socio) : initialForm);
        }
    }, [open, socio]);


    const [errors, setErrors] = useState({});
    const [salvando, setSalvando] = useState(false);

    const handleClose = () => {
        if (salvando) return;
        setActiveStep(0);
        setErrors({});
        onClose();
    };

    

    const handleChange = (field) => (event) => {
        if (field === "cep") {
            if (event.target.value.length === 9) {
                const getEndereco = async () => {
                    const response = await fetch(`https://viacep.com.br/ws/${event.target.value}/json/`);
                    const data = await response.json();
                    setForm((prev) => ({
                        ...prev,
                        rua: data.logradouro || "",
                        bairro: data.bairro || "",
                        cidade: data.localidade || "",
                        estado: data.uf || "",
                    }));
                };
                getEndereco();
            }
        }

        setForm((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));

        setErrors((prev) => ({
            ...prev,
            [field]: "",
        }));
    };

    const validateStep = () => {
        const newErrors = {};

        // =========================
        // STEP 0 - INFORMAÇÕES PESSOAIS
        // =========================
        if (activeStep === 0) {
            if (!form.nome?.trim()) {
                newErrors.nome = "Informe o nome completo.";
            }

            if (!form.cpf?.trim()) {
                newErrors.cpf = "Informe o CPF.";
            }

            if (!form.dataNascimento) {
                newErrors.dataNascimento =
                    "Informe a data de nascimento.";
            }

            if (!form.naturalidade?.trim()) {
                newErrors.naturalidade =
                    "Informe a naturalidade.";
            }
        }

        // =========================
        // STEP 1 - CONTATO E ENDEREÇO
        // =========================
        if (activeStep === 1) {
            if (!form.email?.trim()) {
                newErrors.email = "Informe o e-mail.";
            }

            if (!form.telefone?.trim()) {
                newErrors.telefone =
                    "Informe o telefone ou WhatsApp.";
            }

            if (!form.cep?.trim()) {
                newErrors.cep = "Informe o CEP.";
            }

            if (!form.rua?.trim()) {
                newErrors.rua = "Informe a rua.";
            }

            if (!form.numero?.trim()) {
                newErrors.numero = "Informe o número.";
            }

            if (!form.bairro?.trim()) {
                newErrors.bairro = "Informe o bairro.";
            }

            if (!form.cidade?.trim()) {
                newErrors.cidade = "Informe a cidade.";
            }

            if (!form.estado?.trim()) {
                newErrors.estado = "Informe o estado.";
            }
        }

        // =========================
        // STEP 2 - STATUS CONJUGAL
        // =========================
        if (activeStep === 2) {
            if (!form.estadoCivil?.trim()) {
                newErrors.estadoCivil =
                    "Informe o estado civil.";
            }

            // Regime de bens só é necessário para estados
            // civis que possuem regime de bens.
            const estadosComRegime = [
                "Casado",
                "União Estável",
            ];

            if (
                estadosComRegime.includes(form.estadoCivil) &&
                !form.regimeBens?.trim()
            ) {
                newErrors.regimeBens =
                    "Informe o regime de bens.";
            }

            // CPF do cônjuge também é solicitado quando
            // houver casamento ou união estável.
            if (
                estadosComRegime.includes(form.estadoCivil) &&
                !form.cpfConjugue?.trim()
            ) {
                newErrors.cpfConjugue =
                    "Informe o CPF do cônjuge.";
            }
        }

        // =========================
        // STEP 3 - INFORMAÇÕES DA EMPRESA
        // =========================
        if (activeStep === 3) {

            if (!form.senhaGov?.trim()) {
                newErrors.senhaGov =
                    "Informe a senha do Gov.br.";
            }

            if (!form.enderecoFiscal?.trim()) {
                newErrors.enderecoFiscal =
                    "Informe o endereço fiscal.";
            }

            if (!form.razaoSocial?.trim()) {
                newErrors.razaoSocial =
                    "Informe a razão social.";
            }

            if (!form.nomeFantasia?.trim()) {
                newErrors.nomeFantasia =
                    "Informe o nome fantasia.";
            }
        }

        if (activeStep === 5) {
            if (!form.percentual?.trim()) {
                newErrors.percentual = "Informe o percentual.";
            }

            if (!(+form.percentual > 0)) {
                newErrors.percentual = "O percentual deve ser maior que 0.";
            }
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };



    const handleNext = () => {
        if (!validateStep()) return;

        if (activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
        } else {
            handleSave();
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prev) => prev - 1);
        }
    };

    const handleSave = async () => {
        const novoSocio = form;

        setSalvando(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/atualizarFichaCadastral`, novoSocio, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": localStorage.getItem("token")
                },
            });
            console.log("Sócio salvo com sucesso:", response.data);
            onSave?.(novoSocio);
        } catch (error) {
            console.error("Erro ao salvar o sócio:", error);
        } finally {
            setSalvando(false);
        }

        setActiveStep(0);
        setErrors({});
        onClose();
    };

    const renderStep = () => {
        switch (activeStep) {
            case 0:
                return (
                    <DadosPessoais
                        form={form}
                        errors={errors}
                        handleChange={handleChange}
                    />
                );

            case 1:
                return (
                    <Endereco
                        form={form}
                        errors={errors}
                        handleChange={handleChange}
                    />
                );

            case 2:
                return (
                    <EstadoCivil
                        form={form}
                        errors={errors}
                        handleChange={handleChange}
                    />
                );

            case 3:
                return (
                    <InformacoesEmpresa
                        form={form}
                        errors={errors}
                        handleChange={handleChange}
                    />
                );
            case 4:
                return (
                    <Societario
                        form={form}
                        errors={errors}
                        handleChange={handleChange}
                    />
                );

            case 5:
                return (
                    <Confirmacao
                        form={form}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    borderRadius: {
                        xs: 0,
                        sm: "12px",
                    },
                    overflow: "hidden",
                    margin: {
                        xs: 0,
                        sm: 2,
                    },
                    width: {
                        xs: "100%",
                        sm: "calc(100% - 32px)",
                    },
                    maxHeight: {
                        xs: "100%",
                        sm: "90vh",
                    },
                },
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    px: {
                        xs: 2,
                        sm: 2.5,
                    },
                    py: 1.5,
                    backgroundColor: "#F5F6F8",
                    borderBottom: `1px solid ${BORDER}`,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: {
                                xs: 16,
                                sm: 17,
                            },
                            fontWeight: 700,
                            color: TEXT,
                            lineHeight: 1.2,
                        }}
                    >
                        {editando ? "Editar sócio" : "Adicionar novo sócio"}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 10,
                            color: MUTED,
                            mt: 0.4,
                        }}
                    >
                        Preencha o formulário em {steps.length} passos para
                        vincular o sócio ao contrato social.
                    </Typography>
                </Box>

                <IconButton
                    onClick={handleClose}
                    disabled={salvando}
                    size="small"
                    sx={{
                        color: "#64748B",
                        mt: -0.5,
                        mr: -0.5,
                    }}
                >
                    <CloseIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Box>

            {/* STEPPER */}
            <Box
                sx={{
                    px: {
                        xs: 2,
                        sm: 2.5,
                    },
                    py: 1.5,
                    borderBottom: `1px solid ${BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    overflowX: "auto",
                    padding: "20px",
                }}
            >
                {steps.map((label, index) => {
                    const active = index === activeStep;
                    const completed = index < activeStep;

                    return (
                        <React.Fragment key={label}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.7,
                                    flexShrink: 0,
                                    height: 20,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 23,
                                        height: 23,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor:
                                            active || completed
                                                ? PURPLE
                                                : "#E9EDF1",
                                        color:
                                            active || completed
                                                ? "#FFF"
                                                : "#64748B",
                                        fontSize: 10,
                                        fontWeight: 700,
                                    }}
                                >
                                    {completed ? (
                                        <CheckIcon
                                            sx={{ fontSize: 14 }}
                                        />
                                    ) : (
                                        index + 1
                                    )}
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight:
                                            active || completed
                                                ? 700
                                                : 500,
                                        color:
                                            active || completed
                                                ? PURPLE
                                                : "#667085",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {label}
                                </Typography>
                            </Box>

                            {index < steps.length - 1 && (
                                <Box
                                    sx={{
                                        height: 2,
                                        backgroundColor:
                                            index < activeStep
                                                ? PURPLE
                                                : "#E3E6EA",
                                        flex: 1,
                                        minWidth: 20,
                                        mx: {
                                            xs: 1,
                                            sm: 1.5,
                                        },
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>

            {/* CONTENT */}
            <DialogContent
                sx={{
                    p: 0,
                    backgroundColor: "#FFF",
                }}
            >
                <Box
                    sx={{
                        px: {
                            xs: 2,
                            sm: 2.5,
                        },
                        py: {
                            xs: 2.5,
                            sm: 3,
                        },
                        minHeight: {
                            xs: 330,
                            sm: 350,
                        },
                        boxSizing: "border-box",
                    }}
                >
                    {renderStep()}
                </Box>
            </DialogContent>

            {/* FOOTER */}
            <Box
                sx={{
                    backgroundColor: "#F5F6F8",
                    borderTop: `1px solid ${BORDER}`,
                    px: {
                        xs: 2,
                        sm: 2.5,
                    },
                    py: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={salvando}
                    sx={{
                        color: "#475467",
                        textTransform: "none",
                        fontSize: 11,
                        fontWeight: 500,
                    }}
                >
                    Cancelar
                </Button>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    {activeStep > 0 && (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            disabled={salvando}
                            sx={{
                                color: TEXT,
                                textTransform: "none",
                                fontSize: 11,
                                fontWeight: 600,
                            }}
                        >
                            Voltar
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={salvando}
                        startIcon={
                            salvando && (
                                <CircularProgress size={14} sx={{ color: "inherit" }} />
                            )
                        }
                        sx={{
                            minWidth: 78,
                            height: 34,
                            px: 2,
                            borderRadius: "8px",
                            backgroundColor: PURPLE,
                            color: "#FFF",
                            textTransform: "none",
                            fontSize: 11,
                            fontWeight: 700,
                            boxShadow: "none",
                            "&:hover": {
                                backgroundColor: "#85009E",
                                boxShadow: "none",
                            },
                        }}
                    >
                        {salvando
                            ? "Salvando..."
                            : activeStep === steps.length - 1
                            ? editando
                                ? "Salvar alterações"
                                : "Adicionar sócio"
                            : "Continuar"}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
